import React from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonWithIcon, CreateIcon, actionIconStyles } from '../../../shared/components';
import { TaskStatusDefinition } from '../types/Task';
import {
  getIncomingTaskStatuses,
  getTaskStatusLabel,
  getTaskStatusWorkflowWarnings,
  TaskStatusWorkflowWarning,
} from '../utils/taskStatusTransitions';
import {
  EditableStatusDraft,
  getStatusBadgeStyle,
  NewStatusDraft,
  StatusAutosaveResult,
  StatusDropPlacement,
  StatusValidationState,
} from '../utils/statusManager';
import styles from './TaskList.module.css';

type Props = {
  isOpen: boolean;
  statusModalBusy: boolean;
  savingStatusIds: string[];
  statusAnnouncement: string;
  statusAnnouncementId: number;
  statusValidation: StatusValidationState;
  newStatusDraft: NewStatusDraft;
  setNewStatusDraft: React.Dispatch<React.SetStateAction<NewStatusDraft>>;
  setNewStatusDraftDefault: (checked: boolean) => void;
  statusOptions: TaskStatusDefinition[];
  handleCreateStatus: () => void;
  statusModalError: string | null;
  statusDrafts: EditableStatusDraft[];
  draggedStatusId: string | null;
  dragOverStatusId: string | null;
  dragOverPlacement: StatusDropPlacement;
  setDraggedStatusId: React.Dispatch<React.SetStateAction<string | null>>;
  setDragOverStatusId: React.Dispatch<React.SetStateAction<string | null>>;
  setDragOverPlacement: React.Dispatch<React.SetStateAction<StatusDropPlacement>>;
  handleReorderStatus: (statusId: string, targetIndex: number, restoreFocus?: boolean) => void | Promise<void>;
  handleDeleteStatus: (draft: EditableStatusDraft) => void | Promise<void>;
  statusPreviewDefinitions: TaskStatusDefinition[];
  isStatusDraftDirty: (draft: EditableStatusDraft) => boolean;
  statusAutosaveResults: Record<string, StatusAutosaveResult | undefined>;
  statusDragHandleRefs: React.MutableRefObject<Record<string, HTMLButtonElement | null>>;
  updateStatusDraft: (statusId: string, patch: Partial<EditableStatusDraft>) => void;
  setStatusDraftDefault: (statusId: string, checked: boolean) => void;
  closeStatusModal: () => void;
};

const workflowWarningTranslationKey: Record<TaskStatusWorkflowWarning, string> = {
  isolated: 'workflowWarningIsolated',
  deadEnd: 'workflowWarningDeadEnd',
  unreachable: 'workflowWarningUnreachable',
};

export const StatusManagerModal: React.FC<Props> = ({
  isOpen,
  statusModalBusy,
  savingStatusIds,
  statusAnnouncement,
  statusAnnouncementId,
  statusValidation,
  newStatusDraft,
  setNewStatusDraft,
  setNewStatusDraftDefault,
  statusOptions,
  handleCreateStatus,
  statusModalError,
  statusDrafts,
  draggedStatusId,
  dragOverStatusId,
  dragOverPlacement,
  setDraggedStatusId,
  setDragOverStatusId,
  setDragOverPlacement,
  handleReorderStatus,
  handleDeleteStatus,
  statusPreviewDefinitions,
  isStatusDraftDirty,
  statusAutosaveResults,
  statusDragHandleRefs,
  updateStatusDraft,
  setStatusDraftDefault,
  closeStatusModal,
}) => {
  const { t } = useTranslation(['tasks', 'common']);

  if (!isOpen) {
    return null;
  }

  return (
    <div className={styles.modalOverlay} role="dialog" aria-modal="true" aria-label={t('manageStatuses', { ns: 'tasks' })} onClick={closeStatusModal}>
      <div className={styles.statusModalCard} onClick={event => event.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3 className={styles.modalTitle}>{t('manageStatuses', { ns: 'tasks' })}</h3>
          <button className={styles.modalCloseBtn} type="button" onClick={closeStatusModal} aria-label={t('close', { ns: 'common' })} disabled={statusModalBusy || savingStatusIds.length > 0}>×</button>
        </div>

        <div className={styles.srOnly} aria-live="polite" aria-atomic="true">
          {statusAnnouncement ? <span key={statusAnnouncementId}>{statusAnnouncement}</span> : null}
        </div>

        <div className={styles.statusModalBody}>
          {statusValidation.hasNoDefault ? (
            <div className={`${styles.validationNotice} ${styles.validationNotice_warning}`}>
              {t('workflowRequiresDefaultStatus', { ns: 'tasks' })}
            </div>
          ) : null}

          {statusValidation.hasMultipleDefaults ? (
            <div className={`${styles.validationNotice} ${styles.validationNotice_warning}`}>
              {t('workflowSingleDefaultStatus', { ns: 'tasks' })}
            </div>
          ) : null}

          <div className={styles.statusCreateCard}>
            <div className={styles.statusCreateGrid}>
              <input
                className={`text-input ${styles.statusInput}`}
                value={newStatusDraft.name}
                onChange={(event) => setNewStatusDraft(prev => ({ ...prev, name: event.target.value }))}
                placeholder={t('statusNamePlaceholder', { ns: 'tasks' })}
                maxLength={120}
              />
              <div className={styles.statusFieldGroup}>
                <input
                  className={`text-input ${styles.statusInput}`}
                  value={newStatusDraft.code}
                  onChange={(event) => setNewStatusDraft(prev => ({ ...prev, code: event.target.value }))}
                  placeholder={t('statusCodePlaceholder', { ns: 'tasks' })}
                  maxLength={80}
                />
                {statusValidation.newDraft.effectiveCode ? (
                  <span className={statusValidation.newDraft.duplicateCode ? styles.statusHelperError : styles.statusHelperText}>
                    {statusValidation.newDraft.duplicateCode
                      ? t('statusCodeDuplicateResolved', { ns: 'tasks', code: statusValidation.newDraft.effectiveCode })
                      : t('statusResolvedCodeLabel', { ns: 'tasks', code: statusValidation.newDraft.effectiveCode })}
                  </span>
                ) : null}
              </div>
              <input
                className={`text-input ${styles.colorInput}`}
                type="color"
                value={newStatusDraft.color}
                onChange={(event) => setNewStatusDraft(prev => ({ ...prev, color: event.target.value }))}
                aria-label={t('statusColorLabel', { ns: 'tasks' })}
              />
              <input
                className={`text-input ${styles.orderInput}`}
                type="number"
                value={newStatusDraft.displayOrder}
                onChange={(event) => setNewStatusDraft(prev => ({ ...prev, displayOrder: Number(event.target.value) || 0 }))}
                aria-label={t('statusOrderLabel', { ns: 'tasks' })}
              />
            </div>

            <div className={styles.statusFlagsRow}>
              <label className={styles.statusFlag}><input type="checkbox" checked={newStatusDraft.isDefault} onChange={(event) => setNewStatusDraftDefault(event.target.checked)} />{t('defaultStatusLabel', { ns: 'tasks' })}</label>
              <label className={styles.statusFlag}><input type="checkbox" checked={newStatusDraft.isCompleted} onChange={(event) => setNewStatusDraft(prev => ({ ...prev, isCompleted: event.target.checked }))} />{t('completedStatusLabel', { ns: 'tasks' })}</label>
              <label className={styles.statusFlag}><input type="checkbox" checked={newStatusDraft.requiresAssignee} onChange={(event) => setNewStatusDraft(prev => ({ ...prev, requiresAssignee: event.target.checked }))} />{t('requiresAssigneeLabel', { ns: 'tasks' })}</label>
            </div>

            <select
              className={`text-input ${styles.transitionSelect}`}
              multiple
              value={newStatusDraft.transitionToCodes}
              onChange={(event) => setNewStatusDraft(prev => ({
                ...prev,
                transitionToCodes: Array.from(event.target.selectedOptions).map(option => option.value),
              }))}
            >
              {statusOptions.map(status => <option key={status.id} value={status.code}>{status.name}</option>)}
            </select>

            <button className={`btn btn-primary ${actionIconStyles.buttonWithIcon}`} type="button" onClick={handleCreateStatus} disabled={statusModalBusy || !newStatusDraft.name.trim() || statusValidation.newDraft.duplicateCode}>
              <ButtonWithIcon icon={<CreateIcon />}>{t('addStatus', { ns: 'tasks' })}</ButtonWithIcon>
            </button>
          </div>

          {statusModalError ? <div className="error">{statusModalError}</div> : null}

          <div className={styles.statusDragHint}>{t('statusDragHint', { ns: 'tasks' })}</div>

          <div className={styles.statusList}>
            {statusDrafts.map((draft, index) => {
              const outgoingStatuses = draft.transitionToCodes.filter(code => code && code !== draft.code);
              const incomingStatuses = getIncomingTaskStatuses(draft.code, statusPreviewDefinitions)
                .filter(code => code && code !== draft.code);
              const workflowWarnings = getTaskStatusWorkflowWarnings(draft.code, statusPreviewDefinitions);
              const isDraftSaving = savingStatusIds.includes(draft.id);
              const isDraftDirty = isStatusDraftDirty(draft);
              const draftAutosaveState = isDraftSaving
                ? 'saving'
                : statusAutosaveResults[draft.id] === 'error'
                  ? 'error'
                  : isDraftDirty && !statusValidation.draftIssues[draft.id]?.duplicateCode && Boolean(draft.name.trim())
                    ? 'pending'
                    : statusAutosaveResults[draft.id] === 'saved'
                      ? 'saved'
                      : 'idle';

              return (
                <div
                  key={draft.id}
                  className={`${styles.statusCard} ${draggedStatusId === draft.id ? styles.statusCard_dragging : ''} ${dragOverStatusId === draft.id ? styles.statusCard_dragOver : ''} ${dragOverStatusId === draft.id && dragOverPlacement === 'above' ? styles.statusCard_dropAbove : ''} ${dragOverStatusId === draft.id && dragOverPlacement === 'below' ? styles.statusCard_dropBelow : ''}`}
                  onDragOver={(event) => {
                    if (statusModalBusy || savingStatusIds.length > 0 || draggedStatusId === draft.id) return;
                    event.preventDefault();
                    const bounds = event.currentTarget.getBoundingClientRect();
                    const nextPlacement = event.clientY < bounds.top + (bounds.height / 2) ? 'above' : 'below';
                    if (dragOverStatusId !== draft.id) {
                      setDragOverStatusId(draft.id);
                    }
                    if (dragOverPlacement !== nextPlacement) {
                      setDragOverPlacement(nextPlacement);
                    }
                  }}
                  onDragLeave={(event) => {
                    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                      setDragOverStatusId(current => current === draft.id ? null : current);
                    }
                  }}
                  onDrop={(event) => {
                    event.preventDefault();
                    if (!draggedStatusId || draggedStatusId === draft.id) {
                      setDragOverStatusId(null);
                      return;
                    }

                    const targetIndex = dragOverPlacement === 'above' ? index : index + 1;
                    void handleReorderStatus(draggedStatusId, targetIndex);
                  }}
                >
                  <div className={styles.statusRowTop}>
                    <div className={styles.statusPreview}>
                      <button
                        type="button"
                        className={styles.statusDragHandle}
                        ref={(element) => {
                          statusDragHandleRefs.current[draft.id] = element;
                        }}
                        draggable={!statusModalBusy && savingStatusIds.length === 0}
                        aria-label={t('statusDragHandleLabel', { ns: 'tasks', status: draft.name || t('statusLabel', { ns: 'tasks' }) })}
                        title={t('statusDragHandleLabel', { ns: 'tasks', status: draft.name || t('statusLabel', { ns: 'tasks' }) })}
                        aria-keyshortcuts="ArrowUp ArrowDown Home End"
                        onDragStart={(event) => {
                          if (statusModalBusy || savingStatusIds.length > 0) {
                            event.preventDefault();
                            return;
                          }

                          setDraggedStatusId(draft.id);
                          setDragOverStatusId(draft.id);
                          event.dataTransfer.effectAllowed = 'move';
                          event.dataTransfer.setData('text/plain', draft.id);
                        }}
                        onDragEnd={() => {
                          setDraggedStatusId(null);
                          setDragOverStatusId(null);
                          setDragOverPlacement('below');
                        }}
                        onKeyDown={(event) => {
                          if (statusModalBusy || savingStatusIds.length > 0) {
                            return;
                          }

                          if (event.key === 'ArrowUp') {
                            event.preventDefault();
                            void handleReorderStatus(draft.id, index - 1, true);
                            return;
                          }

                          if (event.key === 'ArrowDown') {
                            event.preventDefault();
                            void handleReorderStatus(draft.id, index + 1, true);
                            return;
                          }

                          if (event.key === 'Home') {
                            event.preventDefault();
                            void handleReorderStatus(draft.id, 0, true);
                            return;
                          }

                          if (event.key === 'End') {
                            event.preventDefault();
                            void handleReorderStatus(draft.id, statusDrafts.length - 1, true);
                          }
                        }}
                      >
                        :::
                      </button>
                      <span className={styles.badge} style={getStatusBadgeStyle(draft.color)}>{draft.name || t('statusLabel', { ns: 'tasks' })}</span>
                      {draftAutosaveState !== 'idle' ? (
                        <span className={`${styles.statusAutosaveState} ${styles[`statusAutosaveState_${draftAutosaveState}`]}`}>
                          {draftAutosaveState === 'pending' ? t('statusAutosavePending', { ns: 'tasks' }) : null}
                          {draftAutosaveState === 'saving' ? t('statusAutosaveSaving', { ns: 'tasks' }) : null}
                          {draftAutosaveState === 'saved' ? t('statusAutosaveSaved', { ns: 'tasks' }) : null}
                          {draftAutosaveState === 'error' ? t('statusAutosaveError', { ns: 'tasks' }) : null}
                        </span>
                      ) : null}
                    </div>
                    <div className={styles.statusActions}>
                      <button className="btn btn-secondary" type="button" onClick={() => handleDeleteStatus(draft)} disabled={statusModalBusy || savingStatusIds.length > 0}>{t('delete', { ns: 'common' })}</button>
                    </div>
                  </div>

                  <div className={styles.statusCreateGrid}>
                    <input className={`text-input ${styles.statusInput}`} value={draft.name} onChange={(event) => updateStatusDraft(draft.id, { name: event.target.value })} placeholder={t('statusNamePlaceholder', { ns: 'tasks' })} />
                    <div className={styles.statusFieldGroup}>
                      <input className={`text-input ${styles.statusInput}`} value={draft.code} onChange={(event) => updateStatusDraft(draft.id, { code: event.target.value })} placeholder={t('statusCodePlaceholder', { ns: 'tasks' })} />
                      {statusValidation.draftIssues[draft.id]?.effectiveCode ? (
                        <span className={statusValidation.draftIssues[draft.id]?.duplicateCode ? styles.statusHelperError : styles.statusHelperText}>
                          {statusValidation.draftIssues[draft.id]?.duplicateCode
                            ? t('statusCodeDuplicateResolved', { ns: 'tasks', code: statusValidation.draftIssues[draft.id]?.effectiveCode })
                            : t('statusResolvedCodeLabel', { ns: 'tasks', code: statusValidation.draftIssues[draft.id]?.effectiveCode })}
                        </span>
                      ) : null}
                    </div>
                    <input className={`text-input ${styles.colorInput}`} type="color" value={draft.color} onChange={(event) => updateStatusDraft(draft.id, { color: event.target.value })} aria-label={t('statusColorLabel', { ns: 'tasks' })} />
                    <input className={`text-input ${styles.orderInput}`} type="number" value={draft.displayOrder} onChange={(event) => updateStatusDraft(draft.id, { displayOrder: Number(event.target.value) || 0 })} aria-label={t('statusOrderLabel', { ns: 'tasks' })} />
                  </div>

                  <div className={styles.statusFlagsRow}>
                    <label className={styles.statusFlag}><input type="checkbox" checked={draft.isDefault} onChange={(event) => setStatusDraftDefault(draft.id, event.target.checked)} />{t('defaultStatusLabel', { ns: 'tasks' })}</label>
                    <label className={styles.statusFlag}><input type="checkbox" checked={draft.isCompleted} onChange={(event) => updateStatusDraft(draft.id, { isCompleted: event.target.checked })} />{t('completedStatusLabel', { ns: 'tasks' })}</label>
                    <label className={styles.statusFlag}><input type="checkbox" checked={draft.requiresAssignee} onChange={(event) => updateStatusDraft(draft.id, { requiresAssignee: event.target.checked })} />{t('requiresAssigneeLabel', { ns: 'tasks' })}</label>
                  </div>

                  {workflowWarnings.length > 0 ? (
                    <div className={styles.workflowWarnings}>
                      <span className={styles.workflowWarningsLabel}>{t('workflowWarningsLabel', { ns: 'tasks' })}</span>
                      <div className={styles.workflowWarningsList}>
                        {workflowWarnings.map(warning => (
                          <span key={`${draft.id}:warning:${warning}`} className={styles.workflowWarningChip}>
                            {t(workflowWarningTranslationKey[warning], { ns: 'tasks' })}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  <label className={styles.transitionLabel}>{t('allowedNextStatusesLabel', { ns: 'tasks' })}</label>
                  <select
                    className={`text-input ${styles.transitionSelect}`}
                    multiple
                    value={draft.transitionToCodes}
                    onChange={(event) => updateStatusDraft(draft.id, {
                      transitionToCodes: Array.from(event.target.selectedOptions).map(option => option.value),
                    })}
                  >
                    {statusOptions.filter(status => status.id !== draft.id).map(status => <option key={status.id} value={status.code}>{status.name}</option>)}
                  </select>

                  <div className={styles.workflowSummary}>
                    <div className={styles.workflowSummaryRow}>
                      <span className={styles.workflowSummaryLabel}>{t('incomingStatusesLabel', { ns: 'tasks' })}</span>
                      <div className={styles.workflowSummaryChips}>
                        {incomingStatuses.length > 0
                          ? incomingStatuses.map(code => (
                            <span key={`${draft.id}:incoming:${code}`} className={styles.workflowChip}>
                              {getTaskStatusLabel(code, statusPreviewDefinitions)}
                            </span>
                          ))
                          : <span className={styles.workflowEmpty}>{t('none', { ns: 'tasks' })}</span>}
                      </div>
                    </div>
                    <div className={styles.workflowSummaryRow}>
                      <span className={styles.workflowSummaryLabel}>{t('outgoingStatusesLabel', { ns: 'tasks' })}</span>
                      <div className={styles.workflowSummaryChips}>
                        {outgoingStatuses.length > 0
                          ? outgoingStatuses.map(code => (
                            <span key={`${draft.id}:outgoing:${code}`} className={styles.workflowChip}>
                              {getTaskStatusLabel(code, statusPreviewDefinitions)}
                            </span>
                          ))
                          : <span className={styles.workflowEmpty}>{t('none', { ns: 'tasks' })}</span>}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};